// Map the backend ICD-10 ConditionCode[] (GET /api/Master/DiagnosisCodes) → the DS
// CodeSearchResult[] the CodeSearchField renders. Defensive over the wire's mixed casing:
// display falls back through description / shortName / PascalCase Definition / code so a row
// always shows something usable rather than blank. Stable id = code (fallback to display+index).
import type { CodeSearchResult } from '@scripsteam/scrips-signal-ds';
import type {
  AllergyCodeOption,
  CodeDisplayName,
  ConditionCode,
  ImmunizationCoding,
  SocialHistoryCodeRow,
} from '@/types/patient-chart-api';
import { normalizeProcedureCode } from '@/patients/lib/procedures';
import { normalizeVaccineCoding } from './to-immunization-request';

/** Only string fields contribute — the DiagnosisCodes wire can carry a nested
 *  Coding object (e.g. `code` as the wrapped Coding), and calling `.trim()` on a
 *  non-string throws. `flattenDiagnosisRow` already coerces to string|null, but
 *  guard here too so `displayOf` can never crash on an unexpected shape. */
function asString(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function displayOf(c: ConditionCode): string {
  return (
    asString(c.display) ||
    asString(c.description) ||
    asString(c.shortName) ||
    asString(c.Definition) ||
    asString(c.code) ||
    ''
  ).trim();
}

export function toCodeSearchResults(
  codes: ConditionCode[] | null | undefined,
): CodeSearchResult[] {
  return (codes ?? []).flatMap((raw, i): CodeSearchResult[] => {
    // GET /api/Master/DiagnosisCodes returns rows WRAPPED as `{ Id, code: <Coding> }`
    // — the row's `code` is the inner Coding object, not a string — so unwrap +
    // stringify every row at the boundary (idempotent on already-flat input).
    // Without this the diagnosis-search path (formerly DxSearchField, retired for the DS
    // AssessmentPlanSearchPanel Diagnosis tab) fed raw wrapped rows straight in and
    // `.trim()` crashed on the nested Coding object (live-caught on "headache").
    const c = flattenDiagnosisRow(raw);
    const display = displayOf(c);
    if (!display) return [];
    const code = c.code?.trim() || undefined;
    return [{ id: code ?? `dx-${i}-${display}`, code, display, system: c.system?.trim() || 'ICD-10' }];
  });
}

/**
 * Lift a master DiagnosisCodes row to a flat ConditionCode. The endpoint
 * (GET /api/Master/DiagnosisCodes) returns rows WRAPPED as `{ Id, code: <real Coding>, imo }`
 * — the row's `code` field IS the Coding (with PascalCase `System` + `Definition`), NOT a string
 * (live-verified 2026-06-19, same wrapped shape Andrew hit 2026-05-20). Storing the raw row makes
 * the create handler nest `code.code: <object>` → backend 400. So unwrap at the search boundary:
 * lift the inner Coding's fields and coalesce PascalCase `System` → lowercase `system` (wire key).
 * Idempotent on already-flat input.
 */
export function flattenDiagnosisRow(row: unknown): ConditionCode {
  if (!row || typeof row !== 'object') {
    return { code: null, Definition: null, display: null, system: null, description: null, shortName: null };
  }
  const r = row as Record<string, unknown>;
  const inner = r.code && typeof r.code === 'object' ? (r.code as Record<string, unknown>) : r;
  const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
  return {
    code: str(inner.code),
    Definition: str(inner.Definition) ?? str(inner.definition),
    display: str(inner.display),
    system: str(inner.system) ?? str(inner.System),
    description: str(inner.description),
    shortName: str(inner.shortName),
  };
}

function allergenDisplayOf(a: AllergyCodeOption): string {
  return (a.display || a.description || a.shortName || a.code || '').trim();
}

/**
 * Map the master AllergyCode[] (GET /api/Master/AllergyCode) → DS CodeSearchResult[] for
 * the IPS-rail allergen picker. The nested `category` Coding is NOT surfaced here — the rail
 * resolves the clicked result id back to the full AllergyCodeOption (carrying category) for the
 * CreateAllergy body. Stable id = code (fallback to display+index for React-key safety). The
 * allergen catalog has no fixed `system`, so unlike ICD-10 there is no default — omit when blank.
 */
export function allergyOptionsToCodeSearchResults(
  options: AllergyCodeOption[] | null | undefined,
): CodeSearchResult[] {
  return (options ?? []).flatMap((a, i): CodeSearchResult[] => {
    const display = allergenDisplayOf(a);
    if (!display) return [];
    const code = a.code?.trim() || undefined;
    const system = a.system?.trim() || undefined;
    return [{ id: code ?? `allergen-${i}-${display}`, code, display, ...(system ? { system } : {}) }];
  });
}

/**
 * Map the master CPT ProcedureCodesList[] (GET /api/Master/ProcedureCodesList) → DS
 * CodeSearchResult[] for the IPS-rail procedure picker. Procedures use **`displayName`**
 * (PascalCase via CodeDisplayName), NOT `display` — so we normalizeProcedureCode each row first
 * (which also unwraps any `{code: Coding}` wrapped row, same defensive guard as the chart picker)
 * then read its displayName for the label. Stable id = code (fallback to display+index for
 * React-key safety). The CPT catalog has no fixed `system`, so — like allergens — omit it
 * when blank rather than defaulting.
 */
export function procedureOptionsToCodeSearchResults(
  options: CodeDisplayName[] | null | undefined,
): CodeSearchResult[] {
  return (options ?? []).flatMap((row, i): CodeSearchResult[] => {
    const flat = normalizeProcedureCode(row);
    if (!flat) return [];
    const display = (flat.displayName || flat.description || flat.shortName || flat.code || '').trim();
    if (!display) return [];
    const code = flat.code?.trim() || undefined;
    const system = flat.system?.trim() || undefined;
    return [{ id: code ?? `proc-${i}-${display}`, code, display, ...(system ? { system } : {}) }];
  });
}

/**
 * Map the master CVX VaccinationCodesList[] (GET /api/Master/VaccinationCodesList) → DS
 * CodeSearchResult[] for the IPS-rail vaccine picker. Immunizations use `display` (like ICD-10,
 * NOT `displayName` like Procedures). Each row is run through normalizeVaccineCoding first — that
 * unwraps any `{code:<Coding>}` wrapped row (the VaccinationCodesList endpoint surfaced this,
 * Andrew 2026-05-20) so both the picker options AND the host's resolveVaccine see the same flat
 * shape. display falls back through description / shortName / code so a row always shows something.
 * Stable id = code (fallback to display+index for React-key safety). CVX carries a fixed `system`
 * URL, so surface it when present.
 */
export function vaccineOptionsToCodeSearchResults(
  options: ImmunizationCoding[] | null | undefined,
): CodeSearchResult[] {
  return (options ?? []).flatMap((row, i): CodeSearchResult[] => {
    const flat = normalizeVaccineCoding(row);
    if (!flat) return [];
    const display = (flat.display || flat.description || flat.shortName || flat.code || '').trim();
    if (!display) return [];
    const code = flat.code?.trim() || undefined;
    const system = flat.system?.trim() || undefined;
    return [{ id: code ?? `vax-${i}-${display}`, code, display, ...(system ? { system } : {}) }];
  });
}

/**
 * Map the master Social-history-type rows (GET /api/Master/GetSocialHistoryList) → DS
 * CodeSearchResult[] for the IPS-rail Social History type picker. The nested `category` Coding is
 * NOT surfaced here — the rail resolves the clicked result id back to the full SocialHistoryCodeRow
 * (carrying category) for the CreateSocialHistory body. Social-history rows use `display` (like
 * ICD-10), falling back through code. Stable id = code (fallback to display+index for React-key
 * safety). The social-history catalog has no fixed `system`, so — like allergens — omit it when
 * blank rather than defaulting.
 */
export function socialTypeOptionsToCodeSearchResults(
  options: SocialHistoryCodeRow[] | null | undefined,
): CodeSearchResult[] {
  return (options ?? []).flatMap((row, i): CodeSearchResult[] => {
    const display = (row.display || row.code || '').trim();
    if (!display) return [];
    const code = row.code?.trim() || undefined;
    const system = row.system?.trim() || undefined;
    return [{ id: code ?? `soc-${i}-${display}`, code, display, ...(system ? { system } : {}) }];
  });
}
